import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SnackBarType } from '../../app/types';
import { randomText } from '../../app/utils';
import {
  changeMessageCheckCondition,
  setNodeData,
  setWhatsappTemplate,
} from '../../features/mainSlice';
import { enqueueSnackbar } from '../../features/notifierSlice';
import useDetectScrollBottom from '../../hooks/useDetectScrollBottom';
import OrganizationService from '../../services/OrganizationService';
import { getParams } from '../shared/templates/WhatsappTemplate';
import DefaultTemplate from './DefaultTemplate';

let lastGetdataLength = 0;
const limit = 50;

function WhatsappTemplateContainer() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { objectId: orgId } = useSelector(store => store.organization);
  const { selectedNode, data: maData } = useSelector(store => store.ma);
  const { messages } = selectedNode?.data || {};
  const { platform } = maData || {};

  const globalTemplateType = useSelector(
    store => store.ma.whatsappTemplate.templateType,
  );
  const messageIndex = useSelector(
    store => store.ma.whatsappTemplate.messageIndex,
  );
  const [list, setList] = useState([]);
  const [localKeyword, setLocalKeyword] = useState('');
  const isTouchBottom = useDetectScrollBottom('default-template-content');
  const [localIsTouchBottom, setLocalIsTouchBottom] = useState(false);
  const [isLazyloading, setIsLazyloading] = useState(false);
  const [localTemplateType, setLocalTemplateType] =
    useState(globalTemplateType);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  const mapData = dataList => {
    return dataList.map(d => {
      if (d.data.contentType === 'text/plain') {
        return {
          ...d,
          templateType: 'text',
          data: d.data.data,
          contentType: d.data.contentType,
        };
      }
      if (d.data.contentType === 'application/x-template') {
        return {
          ...d,
          templateType: d.data.templateType,
          data: d.data.data,
          contentType: d.data.contentType,
        };
      }
      return { ...d, data: d.data.data, contentType: d.data.contentType };
    });
  };

  useEffect(() => {
    async function query() {
      if (
        localTemplateType === 'all' ||
        localTemplateType === globalTemplateType
      ) {
        setIsLoadingTemplate(true);
        const response = await OrganizationService.getWhatsappTemplateList({
          orgId,
          status: 'APPROVED',
        });
        lastGetdataLength = response.length;
        setList(mapData(response));
        setIsLoadingTemplate(false);
      }
    }

    try {
      query();
      setLocalTemplateType(localTemplateType);
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.ERROR,
          message: error?.response?.data?.error ?? error.message,
        }),
      );
    }
  }, []);

  useEffect(() => {
    setLocalIsTouchBottom(isTouchBottom);
  }, [isTouchBottom]);

  useEffect(() => {
    async function query() {
      if (!isLazyloading) {
        setIsLazyloading(true);

        const response = await OrganizationService.getWhatsappTemplateList({
          orgId,
          status: 'APPROVED',
          contentType:
            localTemplateType !== 'all' ? localTemplateType : undefined,
          name: localKeyword === '' ? undefined : localKeyword,
          skip: list.length,
        });
        lastGetdataLength = response.length;
        setList([...list, ...mapData(response)]);
        setIsLazyloading(false);
      }
    }

    if (localIsTouchBottom && isTouchBottom && lastGetdataLength >= limit) {
      setLocalIsTouchBottom(false);
      query();
    }
  }, [isTouchBottom, localIsTouchBottom, localTemplateType, platform]);

  async function handleOnEnter(keyword, templateType) {
    const type = templateType !== 'all' ? templateType : undefined;

    try {
      setIsLoadingTemplate(true);

      const response = await OrganizationService.getWhatsappTemplateList({
        orgId,
        status: 'APPROVED',
        contentType: type,
        name: keyword === '' ? undefined : keyword,
      });
      lastGetdataLength = response.length;

      setList(mapData(response));
      setIsLoadingTemplate(false);
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.ERROR,
          message: error?.response?.data?.error ?? error.message,
        }),
      );
    }
  }

  function handleOnChange(keyword) {
    setLocalKeyword(keyword);
    if (keyword === '') {
      handleOnEnter(keyword, localTemplateType);
    }
  }

  function handleOnChangeType(templateType) {
    setLocalTemplateType(templateType);
    handleOnEnter(localKeyword, templateType);
  }

  function handleOnClose() {
    dispatch(
      setWhatsappTemplate({
        isShow: false,
      }),
    );
  }

  function handleSelectDefaultTemplate(data) {
    dispatch(
      changeMessageCheckCondition(selectedNode?.id, () => {
        dispatch(
          setWhatsappTemplate({
            isShow: false,
          }),
        );
        dispatch(
          setNodeData({
            messages: messages.map((msg, index) => {
              if (index === messageIndex) {
                const params = getParams(data);

                return {
                  name: data.name,
                  contentType: data?.contentType,
                  template:
                    data?.contentType === 'application/x-template'
                      ? randomText()
                      : undefined,
                  data: { ...data?.data, ...params },
                  index,
                  waTemplateId: data?._id,
                };
              }
              return msg;
            }),
          }),
        );
      }),
    );
  }

  return (
    <DefaultTemplate
      title={t('templates.type.whatsappTemplate')}
      localKeyword={localKeyword}
      list={list}
      onEnter={keyword => {
        handleOnEnter(keyword, localTemplateType);
      }}
      onChange={handleOnChange}
      onChangeType={handleOnChangeType}
      templateType={localTemplateType}
      onClose={handleOnClose}
      onSelectDefaultTemplate={handleSelectDefaultTemplate}
      platform={platform}
      isLoading={isLoadingTemplate}
      isSend
      isWhatsappTemplate
      isCopy={false}
      isEdit={false}
    />
  );
}

export default WhatsappTemplateContainer;
